import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventReportApi } from '@/features/events/eventReportApi';
import { EventReportView } from '@/features/events/EventReportView';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 02 — anonymous public landing for a shared event report. Renders the
 * same EventReportView as the admin page, no admin chrome. The branding
 * block of the payload drives the colors / logo.
 */
export function PublicEventReportPage() {
  const { token } = useParams<{ token: string }>();
  const t = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ['eventReport', 'public', token],
    queryFn: () => eventReportApi.publicByToken(token ?? ''),
    enabled: !!token && token.length >= 16,
    retry: false,
  });

  if (!token || token.length < 16) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--color-bg-default)] text-center">
        <p>{t('eventReport.publicInvalid')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-[var(--color-fg-muted)]">
        {t('common.loading')}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--color-bg-default)] px-6 text-center">
        <div>
          <h1 className="text-2xl font-bold">
            {t('eventReport.publicNotFound.title')}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            {t('eventReport.publicNotFound.body')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 print:p-0">
      <EventReportView report={data} variant="public" />
    </main>
  );
}
